/**
 * Names beside codes — the store and article names of ONE run, read once and shared by every
 * screen of the run (usability audit 2026-10-02 §B1–B3, DEC-111).
 *
 * **Where the names come from.** The run itself, by key (bata-platform ADR 6177):
 * `GET /inseason/runs/{run_id}/names?store_key=…&article_key=…` names any asked key with the rule a
 * proposal line's name is resolved by (ADR 6080) — the door master in force, the attribute row in
 * force on the run's date, the report's own absent reasons. A name is a property of its KEY within
 * the run, so one dictionary per run serves every row of every screen, and the exports, which read
 * the run's reports whole (`report-attributes.ts`), name a row identically.
 *
 * **Only the keys a screen draws, never the book (audit 4, 2026-10-13).** Until then a key no
 * loaded line named walked `report/replenishment` — the run's whole book, 19 requests of 1000 on
 * CO, tens of seconds — to name a few dozen keys. Now a lookup the screen's lines do not answer
 * registers the key, and the keys of a render are asked in one request (up to 100 of each kind;
 * more go in more requests). Nothing walks a report on a screen any more: only a FILE reads the
 * report whole (`readRunNames`, `collectFresh`), because a file needs every row.
 *
 * **One read per key set per session.** A name is a property of the run's key (above), so the
 * names cannot change under it: every request is fresh for the session (`SESSION_LONG_READ`) and
 * kept an hour after the last screen leaves, and is a `BACKGROUND_READ`: the picker's button and
 * the placeholders' `aria-busy` settle when the page's own data has. The shell's progress line
 * still counts it.
 *
 * **The lines a screen holds name their own keys** (bata-platform ADR 6080). A proposal line carries
 * `store_name`, `article_name` and their absent reasons — the report's own resolution — so a screen
 * that holds the run's lines (`NameSources.lines`) names every key on them with NO request. The names
 * read is made only for a key no loaded line names, and until it answers that key reads as «reading»,
 * never as absent.
 *
 * **Codes first, names when they arrive.** Nothing waits on this read: until it answers every
 * row shows its code. A name the platform does not publish keeps the code and says why — the
 * report's own absent reason (`not_published`, `store_not_in_location_master`,
 * `attributes_not_landed`), `not_in_report` for a key the answer does not carry,
 * `report_unreadable` when the names could not be read. Never a guess.
 *
 * The drill headers still ask the network-health read for a key the names leave unnamed
 * (`useEntityName`), as before.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKGROUND_READ, callOperation, collectFresh, SESSION_LONG_READ, useApiQueries, useApiQuery } from '@/api';
import {
  replenishmentReport, runNamesReport, transfersReport,
  type ReplenishmentReportRow, type RunNamesResponse, type TransferReportRow,
} from '@/api/generated';
import {
  dimensionLabelOf, NETWORK_HEALTH_PROPOSED_OPERATION, NETWORK_HEALTH_READ, networkHealthParams,
} from '@/api/pending/network-health';
import type { ExportCell, ExportColumn } from '@/export';
import { i18next } from '@/i18n';
import type { DataColumn } from '@/planning';

import { whyReasonKey } from './why';

/** A key neither of the run's reports carries. */
export const NOT_IN_REPORT = 'not_in_report';
/** The run's report could not be read: every key keeps its code. */
export const REPORT_UNREADABLE = 'report_unreadable';

export type NameKind = 'store' | 'article';

/** One key as the screen shows it: its code, and its name or why there is none. */
export interface Named {
  readonly code: string;
  /** The platform's name; `null` while the read is pending or when it publishes none. */
  readonly name: string | null;
  /** Why `name` is `null` — a platform or UI absent reason; `null` when named or still reading. */
  readonly reason: string | null;
}

interface Entry {
  readonly name: string | null;
  readonly reason: string | null;
}

/** The run's names, by store key and by article key. */
export interface NameIndex {
  readonly stores: ReadonlyMap<string, Entry>;
  readonly articles: ReadonlyMap<string, Entry>;
}

type ReplenishmentNames = Pick<ReplenishmentReportRow, 'store_key' | 'store_name' | 'store_name_absent_reason' | 'article_key' | 'article_name' | 'article_name_absent_reason'>;
/**
 * The names a proposal line carries (platform ADR 6080) — the report's own resolution. A store's or
 * an article's roll-up (`ProposalGroup`, ADR 6179) names the one key it is of, and carries none of the other.
 */
export interface LineNames {
  readonly store_key?: string | null | undefined;
  readonly store_name?: string | null | undefined;
  readonly store_name_absent_reason?: string | null | undefined;
  readonly article_key?: string | null | undefined;
  readonly article_name?: string | null | undefined;
  readonly article_name_absent_reason?: string | null | undefined;
}
type TransferNames = Pick<TransferReportRow,
  | 'donor_store_key' | 'donor_store_name' | 'donor_store_name_absent_reason'
  | 'recipient_store_key' | 'recipient_store_name' | 'recipient_store_name_absent_reason'
  | 'article_key' | 'article_name' | 'article_name_absent_reason'>;

/** A name beats an absence; the first absence read is kept (both reports read one master). */
function put(map: Map<string, Entry>, key: string, name: string | null, reason: string | null): void {
  const known = map.get(key);
  if (known?.name !== undefined && known.name !== null) return;
  if (name !== null && name !== '') {
    map.set(key, { name, reason: null });
    return;
  }
  if (known === undefined) map.set(key, { name: null, reason: reason ?? NOT_IN_REPORT });
}

/** The run's names from its two reports, rows already read. Pure. */
export function indexNames(
  replenishment: readonly ReplenishmentNames[] = [],
  transfers: readonly TransferNames[] = [],
): NameIndex {
  const stores = new Map<string, Entry>();
  const articles = new Map<string, Entry>();
  for (const row of replenishment) {
    put(stores, row.store_key, row.store_name, row.store_name_absent_reason);
    put(articles, row.article_key, row.article_name, row.article_name_absent_reason);
  }
  for (const row of transfers) {
    put(stores, row.donor_store_key, row.donor_store_name, row.donor_store_name_absent_reason);
    put(stores, row.recipient_store_key, row.recipient_store_name, row.recipient_store_name_absent_reason);
    put(articles, row.article_key, row.article_name, row.article_name_absent_reason);
  }
  return { stores, articles };
}

/**
 * What proposal lines say of their keys. A line that names its store or article, or says why it
 * cannot, is the answer for that key; a line that says neither (a platform that does not carry the
 * fields yet) says nothing, and the key stays for the report to name. Pure.
 */
export function indexLineNames(lines: readonly LineNames[]): NameIndex {
  const stores = new Map<string, Entry>();
  const articles = new Map<string, Entry>();
  const take = (map: Map<string, Entry>, key: string, name: string | null | undefined, reason: string | null | undefined): void => {
    if (typeof name === 'string' && name !== '') put(map, key, name, null);
    else if (typeof reason === 'string' && reason !== '') put(map, key, null, reason);
  };
  for (const line of lines) {
    if (typeof line.store_key === 'string') take(stores, line.store_key, line.store_name, line.store_name_absent_reason);
    if (typeof line.article_key === 'string') take(articles, line.article_key, line.article_name, line.article_name_absent_reason);
  }
  return { stores, articles };
}

/** `base` with what `over` knows laid on it: a name beats an absence, and the first absence read is kept. Pure. */
export function mergeIndex(base: NameIndex | null, over: NameIndex | null): NameIndex | null {
  if (base === null) return over;
  if (over === null) return base;
  const stores = new Map(base.stores);
  const articles = new Map(base.articles);
  for (const [key, entry] of over.stores) put(stores, key, entry.name, entry.reason);
  for (const [key, entry] of over.articles) put(articles, key, entry.name, entry.reason);
  return { stores, articles };
}

/**
 * The index with the network-health read's labels added for `kind` — only where the reports give
 * no name (both read the same masters; the reports are the files' source, so they win). Pure.
 */
export function withLabels(index: NameIndex | null, kind: NameKind, labels: ReadonlyMap<string, string>): NameIndex | null {
  if (labels.size === 0) return index;
  const base = index ?? { stores: new Map<string, Entry>(), articles: new Map<string, Entry>() };
  const merged = new Map(kind === 'store' ? base.stores : base.articles);
  for (const [key, label] of labels) {
    const known = merged.get(key);
    if (known === undefined || known.name === null) merged.set(key, { name: label, reason: null });
  }
  return kind === 'store' ? { ...base, stores: merged } : { ...base, articles: merged };
}

export type NamesState = 'reading' | 'ready' | 'unreadable';

/** One key's name from an index in a state. Pure. */
export function nameIn(index: NameIndex | null, state: NamesState, kind: NameKind, code: string): Named {
  const entry = index === null ? undefined : (kind === 'store' ? index.stores : index.articles).get(code);
  if (entry?.name !== undefined && entry.name !== null) return { code, ...entry };
  // Still reading: a key not named yet may be named by the report that has not answered.
  if (state === 'reading') return { code, name: null, reason: null };
  if (entry !== undefined) return { code, ...entry };
  // A report that failed may have carried the key: say so rather than «not in the report».
  return { code, name: null, reason: state === 'unreadable' || index === null ? REPORT_UNREADABLE : NOT_IN_REPORT };
}

/** What a screen holds: the run's names, looked up by key. */
export interface RunNames {
  readonly state: NamesState;
  readonly store: (code: string) => Named;
  readonly article: (code: string) => Named;
}

/** The names of no run: every key is its code, nothing is being read. */
export const NO_NAMES: RunNames = {
  state: 'unreadable',
  store: (code) => ({ code, name: null, reason: REPORT_UNREADABLE }),
  article: (code) => ({ code, name: null, reason: REPORT_UNREADABLE }),
};

/** Builds the lookups over an index. Pure — the hook's half a test can call. */
export function runNamesOf(index: NameIndex | null, state: NamesState): RunNames {
  return {
    state,
    store: (code) => nameIn(index, state, 'store', code),
    article: (code) => nameIn(index, state, 'article', code),
  };
}

/** Which of the run's reports a screen needs: a proposal screen has no use for the transfers'. */
export interface NameSources {
  readonly replenishment?: boolean;
  readonly transfers?: boolean;
  /**
   * The run's proposal lines the screen already holds. They name their own keys; the replenishment
   * report is then walked only for a key no line names. Left out, the report is walked, as before.
   */
  readonly lines?: readonly LineNames[];
}

const BOTH: NameSources = { replenishment: true, transfers: true };

/**
 * One network-health page that names a level's keys the reports may not carry — the articles a
 * store holds positions for with no line in the run (`groupBy: 'article'`, `storeKey`). One request,
 * the read's largest page; a key past it keeps the reports' answer. Only a FILE asks for this now
 * (`readRunNames`): a screen asks the run for the names of the keys it draws.
 */
export interface NameAugment {
  readonly groupBy: NameKind;
  readonly storeKey?: string | null;
}

/** The largest network-health page (`limit` ≤ 500 in the pinned spec). */
const AUGMENT_LIMIT = 500;

/** The most keys of one kind the names read takes in a request (ADR 6177: ≤ 100 per kind). */
export const NAMES_PER_REQUEST = 100;

/** The keys a screen has asked the run to name, in the order it asked — append-only, so a chunk never shifts. */
interface Wanted {
  /** Which book of keys this is (one per run a hook has been given), so a stale snapshot is never read. */
  readonly epoch: number;
  /** How many keys had been asked for when this was published. */
  readonly version: number;
  readonly stores: readonly string[];
  readonly articles: readonly string[];
}

const NOT_WANTED: Wanted = { epoch: -1, version: 0, stores: [], articles: [] };

/** The keys being noted while a screen renders. Mutated in render (idempotently) and published after it. */
interface Book {
  readonly epoch: number;
  readonly runId: string | null;
  version: number;
  readonly stores: string[];
  readonly articles: string[];
  readonly seen: Set<string>;
}

let books = 0;
const newBook = (runId: string | null): Book => ({ epoch: books++, runId, version: 0, stores: [], articles: [], seen: new Set() });

/** `keys` cut into requests of {@link NAMES_PER_REQUEST}. Pure. */
export function nameChunks(stores: readonly string[], articles: readonly string[]): readonly { readonly stores: readonly string[]; readonly articles: readonly string[] }[] {
  const count = Math.max(Math.ceil(stores.length / NAMES_PER_REQUEST), Math.ceil(articles.length / NAMES_PER_REQUEST));
  return Array.from({ length: count }, (_, index) => ({
    stores: stores.slice(index * NAMES_PER_REQUEST, (index + 1) * NAMES_PER_REQUEST),
    articles: articles.slice(index * NAMES_PER_REQUEST, (index + 1) * NAMES_PER_REQUEST),
  }));
}

/**
 * The names a `GET /inseason/runs/{run_id}/names` answer carries, as an index (ADR 6177). The
 * answer names each asked key with the rule a proposal line's name is resolved by, so a key it
 * cannot name carries the report's own reason — `not_published`, `store_not_in_location_master`,
 * `attributes_not_landed` — which `put` keeps. Pure.
 */
export function indexNamesResponse(answers: readonly Partial<Pick<RunNamesResponse, 'stores' | 'articles'>>[]): NameIndex {
  const stores = new Map<string, Entry>();
  const articles = new Map<string, Entry>();
  // An answer that carries no list for a kind names nothing of it: its keys read `not_in_report`.
  for (const answer of answers) {
    for (const row of answer.stores ?? []) put(stores, row.store_key, row.store_name, row.store_name_absent_reason);
    for (const row of answer.articles ?? []) put(articles, row.article_key, row.article_name, row.article_name_absent_reason);
  }
  return { stores, articles };
}

/**
 * The run's names, by the key a screen draws. `runId` `null` reads nothing.
 *
 * **Names are asked for by key, never by walking the report (platform ADR 6177).** The replenishment
 * report is the run's whole book — 19 requests of 1000 on CO — and a screen draws a few dozen keys.
 * So a lookup that the screen's own proposal lines (`sources.lines`, ADR 6080) do not answer
 * registers the key, and once the render has settled the keys registered are asked of
 * `GET /inseason/runs/{run_id}/names` — one request, `store_key` and `article_key` repeated, up to
 * {@link NAMES_PER_REQUEST} of each kind (a longer list goes in as many requests). The names are
 * the proposal line's own resolution, so the same key reads the same name on every screen; a key
 * the platform cannot name keeps its code and carries the platform's reason.
 *
 * Until the answer arrives a key reads as «reading», never as absent; a request that fails leaves
 * its keys `report_unreadable`. Both reads are fresh for the session (`SESSION_LONG_READ`): a name
 * is a property of the run's key. The files do not use this: they read the report whole
 * (`readRunNames`).
 */
export function useRunNames(runId: string | null, sources: NameSources = BOTH): RunNames {
  const lines = runId !== null && (sources.replenishment === true || sources.transfers === true) ? sources.lines : undefined;
  const lineIndex = useMemo(() => (lines === undefined ? null : indexLineNames(lines)), [lines]);
  const wantsNames = runId !== null && (sources.replenishment === true || sources.transfers === true);

  // The keys the render has asked for: noted while rendering (idempotent), published once, after it.
  const noted = useRef<Book | null>(null);
  const queued = useRef(false);
  const [published, setPublished] = useState<Wanted>(NOT_WANTED);
  if (noted.current === null || noted.current.runId !== runId) noted.current = newBook(runId);
  const current = noted.current;
  const note = useCallback((kind: NameKind, code: string): void => {
    const book = noted.current;
    const id = `${kind}:${code}`;
    if (book === null || book.runId === null || book.seen.has(id)) return;
    book.seen.add(id);
    (kind === 'store' ? book.stores : book.articles).push(code);
    book.version += 1;
    if (queued.current) return;
    queued.current = true;
    queueMicrotask(() => {
      queued.current = false;
      const latest = noted.current;
      if (latest === null) return;
      setPublished({ epoch: latest.epoch, version: latest.version, stores: [...latest.stores], articles: [...latest.articles] });
    });
  }, []);

  const wanted = published.epoch === current.epoch ? published : NOT_WANTED;
  const chunks = useMemo(() => nameChunks(wanted.stores, wanted.articles), [wanted]);
  const answers = useApiQueries(
    runNamesReport,
    chunks.map((chunk) => ({
      path: { run_id: runId ?? '' },
      query: {
        ...(chunk.stores.length === 0 ? {} : { store_key: chunk.stores }),
        ...(chunk.articles.length === 0 ? {} : { article_key: chunk.articles }),
      },
    })),
    { enabled: wantsNames, ...SESSION_LONG_READ, ...BACKGROUND_READ },
  );
  const pending = answers.some((read) => read.isPending);
  const failed = answers.some((read) => read.isError);
  const signature = answers.map((read) => `${read.status}:${read.dataUpdatedAt}`).join('|');
  const fetched = useMemo(
    () => indexNamesResponse(answers.flatMap((read) => (read.data === undefined ? [] : [read.data]))),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` stands for the answers' data
    [signature],
  );
  const index = useMemo(() => mergeIndex(lineIndex, fetched), [lineIndex, fetched]);

  return useMemo(() => {
    if (!wantsNames) return NO_NAMES;
    // Reading while a key has been asked for and not yet sent, or sent and not yet answered.
    const stateNow = (): NamesState => {
      if (pending || (noted.current?.version ?? 0) !== wanted.version) return 'reading';
      return failed ? 'unreadable' : 'ready';
    };
    const lookup = (kind: NameKind) => (code: string): Named => {
      const known = (kind === 'store' ? index?.stores : index?.articles)?.get(code);
      if (known !== undefined) return nameIn(index, 'ready', kind, code);
      note(kind, code);
      return nameIn(index, stateNow(), kind, code);
    };
    return { get state() { return stateNow(); }, store: lookup('store'), article: lookup('article') };
  }, [wantsNames, pending, failed, wanted, index, note]);
}

/**
 * The same names outside React — for a file built on demand. Reads what `useRunNames` reads (the
 * reports asked for, the network-health page when augmented) and never rejects: an unread report
 * leaves its keys `report_unreadable`, exactly as the screen says it.
 */
export async function readRunNames(
  runId: string | null, sources: NameSources = BOTH, augment: NameAugment | null = null,
): Promise<RunNames> {
  if (runId === null || (sources.replenishment !== true && sources.transfers !== true)) return NO_NAMES;
  const path = { path: { run_id: runId } };
  const settle = async <T,>(read: Promise<T>): Promise<T | null> => {
    try { return await read; } catch { return null; }
  };
  const [proposals, transfers, groups] = await Promise.all([
    sources.replenishment === true ? settle(collectFresh(replenishmentReport, path, i18next.language)) : Promise.resolve(undefined),
    sources.transfers === true ? settle(collectFresh(transfersReport, path, i18next.language)) : Promise.resolve(undefined),
    augment === null || NETWORK_HEALTH_READ === null
      ? Promise.resolve(null)
      : settle(callOperation(NETWORK_HEALTH_READ, networkHealthParams(runId, augment.groupBy, { storeKey: augment.storeKey ?? null, limit: AUGMENT_LIMIT }))),
  ]);
  const failed = proposals === null || transfers === null;
  let index: NameIndex | null = proposals === null && transfers === null ? null : indexNames(proposals ?? [], transfers ?? []);
  if (augment !== null && groups !== null) {
    const labels = new Map<string, string>();
    for (const group of groups.items) if (group.dimensionKey !== null && group.dimensionLabel !== null) labels.set(group.dimensionKey, group.dimensionLabel);
    index = withLabels(index, augment.groupBy, labels);
  }
  return runNamesOf(index, failed ? 'unreadable' : 'ready');
}

/**
 * One store's or article's name for a drill header: the run's reports first, else the
 * network-health read's `dimensionLabel` (one group, filtered to the key) — which also names a key
 * the run has positions for but no line. `null` while neither has answered or neither publishes one.
 */
export function useEntityName(runId: string | null, names: RunNames, kind: NameKind, code: string | null): string | null {
  const fromReport = code === null ? null : (kind === 'store' ? names.store(code) : names.article(code)).name;
  const ask = runId !== null && code !== null && fromReport === null && names.state !== 'reading' && NETWORK_HEALTH_READ !== null;
  const filters = kind === 'store' ? { storeKey: code, limit: 1 } : { articleKey: code, limit: 1 };
  const query = useApiQuery(
    NETWORK_HEALTH_READ ?? NETWORK_HEALTH_PROPOSED_OPERATION,
    networkHealthParams(runId ?? '', kind, filters),
    { enabled: ask },
  );
  return fromReport ?? (ask ? dimensionLabelOf(query.data, code) : null);
}

// ── how a name is shown ──────────────────────────────────────────────────────

const UI_REASON_KEY: Readonly<Record<string, string>> = {
  [NOT_IN_REPORT]: 'inseason.names.absent.notInReport',
  [REPORT_UNREADABLE]: 'inseason.names.absent.reportUnreadable',
};

/** The words for why a name is absent; still reading has its own. */
export function nameAbsentKey(named: Named): string {
  if (named.reason === null) return 'inseason.names.absent.reading';
  return UI_REASON_KEY[named.reason] ?? whyReasonKey(named.reason);
}

type Translate = (key: string, values: Record<string, string>) => string;

/** «Santafé (1203)» where named, the code alone otherwise — for sentences, lists and labels. */
export function withCode(t: Translate, named: Named): string {
  return named.name === null ? named.code : t('inseason.names.withCode', { name: named.name, code: named.code });
}

/** The same as `withCode`, as a hook-bound function. */
export function useWithCode(): (named: Named) => string {
  const { t } = useTranslation();
  return (named) => withCode(t, named);
}

/**
 * How a collected filter offers one of its values — «Santafé (1203)» for `1203`.
 *
 * Every ranked table whose store or article filter COLLECTS values (client request, 2026-10-06)
 * needs the same thing: the code on the URL, the name on the control. Written once so the five
 * surfaces that do it cannot drift into five spellings of the same label, and so a table whose
 * name read has not answered yet degrades identically everywhere — to the code, which is what
 * the filter always matched on anyway.
 */
export function nameLabel(t: Translate, named: (code: string) => Named): (code: string) => string {
  return (code) => withCode(t, named(code));
}

/**
 * A name's cell: the name, or — absent — «No name» in words (ADR 9061; «Reading» while the report is
 * still being read) whose tooltip and screen-reader text say why (the code is in its own column
 * beside it).
 */
export function NameCell({ named, header = false, className = 'py-1' }: {
  readonly named: Named;
  /** The row's label cell, `<th scope="row">`. */
  readonly header?: boolean;
  readonly className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const Cell = header ? 'th' : 'td';
  const scope = header ? { scope: 'row' as const } : {};
  if (named.name !== null) {
    return <Cell {...scope} className={`${className} text-start font-normal`} data-name-of={named.code}>{named.name}</Cell>;
  }
  const why = t(nameAbsentKey(named));
  return (
    <Cell {...scope} className={`${className} text-ink-muted text-start font-normal`} title={why} data-name-absent={named.reason ?? 'reading'}>
      <span aria-hidden>{t(named.reason === null ? 'overview.trend.pending' : 'inseason.names.absent.label')}</span>
      <span className="sr-only">{why}</span>
    </Cell>
  );
}

/**
 * A name inside a sentence or a cell that says more than the name: «Santafé (1203)», or the code
 * alone — in mono — whose tooltip says why there is no name.
 */
export function NameText({ named }: { readonly named: Named }): React.JSX.Element {
  const { t } = useTranslation();
  if (named.name !== null) return <span data-name-of={named.code}>{withCode(t, named)}</span>;
  const why = t(nameAbsentKey(named));
  return (
    <span className="font-numeric" title={why} data-name-absent={named.reason ?? 'reading'}>
      {named.code}
      <span className="sr-only">{` — ${why}`}</span>
    </span>
  );
}

/** The header of each name column — the export's own words (`report-attributes.ts`), so screen and file agree. */
export const NAME_HEADER_KEY = {
  store: 'inseason.reportAttributes.column.storeName',
  article: 'inseason.reportAttributes.column.articleName',
  donor: 'inseason.reportAttributes.column.donorStoreName',
  recipient: 'inseason.reportAttributes.column.recipientStoreName',
} as const;

/**
 * A sortable, filterable name column for a `DataTable`: its value is the name (absent sorts last
 * and exports as not reported), its cell the name or why there is none.
 */
export function nameColumn<T>(
  id: string, headerKey: string, named: (row: T) => Named,
  options: { readonly descriptionKey: string; readonly header?: boolean },
): DataColumn<T> {
  return {
    id,
    headerKey,
    // The (i) of the column (DEC-111): required, so no name column ships undescribed.
    descriptionKey: options.descriptionKey,
    kind: 'text',
    value: (row) => named(row).name,
    cell: (row) => <NameCell named={named(row)} {...(options.header === true ? { header: true } : {})} />,
  };
}

// ── the same names in a file ─────────────────────────────────────────────────

/** Each name column a file can carry: its key, header and guide sentence (the report files' own). */
const NAME_EXPORT: Readonly<Record<'store' | 'article' | 'donor' | 'recipient', { readonly key: string; readonly description: string }>> = {
  store: { key: 'store_name', description: 'inseason.columnGuide.reportAttributes.storeName' },
  article: { key: 'article_name', description: 'inseason.columnGuide.reportAttributes.articleName' },
  donor: { key: 'donor_store_name', description: 'inseason.columnGuide.reportAttributes.donorStoreName' },
  recipient: { key: 'recipient_store_name', description: 'inseason.columnGuide.reportAttributes.recipientStoreName' },
};

export type NameExportRole = keyof typeof NAME_EXPORT;

/**
 * The name columns of a file, then each one's absent reason (technical, «Tecnico» in the workbook):
 * the same words, keys and reasons as the replenishment and transfer files (`report-attributes.ts`).
 */
export function nameExportColumns(roles: readonly NameExportRole[]): ExportColumn[] {
  return [
    ...roles.map((role): ExportColumn => ({
      key: NAME_EXPORT[role].key, headerKey: NAME_HEADER_KEY[role], kind: 'text', descriptionKey: NAME_EXPORT[role].description,
    })),
    ...roles.map((role): ExportColumn => ({
      key: `${NAME_EXPORT[role].key}_absent_reason`,
      headerKey: 'inseason.reportAttributes.column.absentReason',
      headerValues: { column: NAME_EXPORT[role].key },
      kind: 'text',
      descriptionKey: 'inseason.columnGuide.reportAttributes.absent_reason',
    })),
  ];
}

/** One row's name cells, in `nameExportColumns`' order: each name, then each absent reason. */
export function nameExportCells(named: readonly Named[]): ExportCell[] {
  return [
    ...named.map((one): ExportCell => (one.name === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: one.name })),
    ...named.map((one): ExportCell => ({ kind: 'text', value: one.name === null ? one.reason ?? REPORT_UNREADABLE : '' })),
  ];
}
